import { DurationInput } from '@/components/form/inputs/DurationInput';
import { SectionLabel } from '@/components/ui/section-label';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card/Card';
import { RequiredMultiGroup } from '@/components/form/inputs/RequiredMultiGroup';
import { SimpleInput } from '@/components/form/inputs/SimpleInput';
import { ComboboxInput } from '@/components/form/inputs/ComboboxInput';
import { OPERATION_OPTIONS } from '@/features/traceability/data/operationOptions';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { Clock, Package, Gauge, Settings2 } from 'lucide-react';

type GeneralTabProps = {
  values: Record<string, unknown>;
  onChange: (name: string, value: unknown) => void;
};

export const GeneralTab = forwardRef(function GeneralTab(
  { values, onChange }: GeneralTabProps,
  ref
) {
  const durationInput1Ref = useRef<{ validate: () => boolean }>(null);
  const durationInput2Ref = useRef<{ validate: () => boolean }>(null);
  const multiInputRef1 = useRef<{ validate: () => boolean }>(null);
  const multiInputRef2 = useRef<{ validate: () => boolean }>(null);
  const input1Ref = useRef<{ validate: () => boolean }>(null);
  const input2Ref = useRef<{ validate: () => boolean }>(null);
  const input3Ref = useRef<{ validate: () => boolean }>(null);
  const input4Ref = useRef<{ validate: () => boolean }>(null);

  const durationInputRefs = [durationInput1Ref, durationInput2Ref];
  const inputRefs = [input1Ref, input2Ref, input3Ref];
  const { tokenService } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    if (!values['operator']) {
      values['operator'] = tokenService?.getUserMail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(ref, () => ({
    validate: () => {
      const v1 = durationInput1Ref.current?.validate() ?? true;
      const v2 = durationInput2Ref.current?.validate() ?? true;
      const v3 = multiInputRef1.current?.validate() ?? true;
      const v31 = multiInputRef2.current?.validate() ?? true;
      const v4 = input1Ref.current?.validate() ?? true;
      const v5 = input2Ref.current?.validate() ?? true;
      const v6 = input3Ref.current?.validate() ?? true;
      const v7 = input4Ref.current?.validate() ?? true;
      return v1 && v2 && v3 && v31 && v4 && v5 && v6 && v7;
    },
  }));

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      <Card padding="sm">
        <div className="flex flex-col gap-2">
          <RequiredMultiGroup ref={multiInputRef2} minRequired={1} inputRefs={durationInputRefs}>
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1">
                <SectionLabel icon={Clock}>
                  {t('traceability.generalTab.durationsTitle')}
                </SectionLabel>
                <p className="text-sm text-muted-foreground">
                  {t('traceability.generalTab.durationsDescription')}
                </p>
              </div>
              <Badge tone="primary" caps>
                {t('traceability.generalTab.durationsRequiredCounter')}
              </Badge>
            </div>
            <div className="mt-3 flex flex-col gap-2">
              <DurationInput
                ref={durationInput1Ref}
                name="duration_total"
                label={t('traceability.generalTab.totalDurationLabel')}
                value={values['duration_total'] as string}
                onChange={(val: string | number) => onChange('duration_total', val)}
                requiredMulti={true}
              />
              <DurationInput
                ref={durationInput2Ref}
                name="duration_piece"
                label={t('traceability.generalTab.pieceDurationLabel')}
                value={values['duration_piece'] as string}
                onChange={(val: string | number) => onChange('duration_piece', val)}
                requiredMulti={true}
              />
            </div>
          </RequiredMultiGroup>
        </div>
      </Card>

      <Card padding="sm">
        <div className="flex flex-col gap-2">
          <RequiredMultiGroup ref={multiInputRef1} minRequired={1} inputRefs={inputRefs}>
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1">
                <SectionLabel icon={Package}>
                  {t('traceability.generalTab.productionTitle')}
                </SectionLabel>
                <p className="text-sm text-muted-foreground">
                  {t('traceability.generalTab.productionDescription')}
                </p>
              </div>
              <Badge tone="primary" caps>
                {t('traceability.generalTab.productionRequiredCounter')}
              </Badge>
            </div>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <SimpleInput
                ref={input1Ref}
                name="pieces"
                label={t('traceability.generalTab.piecesLabel')}
                type="number"
                value={values['pieces'] as number}
                onChange={(val) => onChange('pieces', val)}
                min={0}
                step={1}
                requiredMulti={true}
              />
              <SimpleInput
                ref={input2Ref}
                name="volume"
                label={t('traceability.generalTab.volumeLabel')}
                type="number"
                value={values['volume'] as number}
                onChange={(val) => onChange('volume', val)}
                min={0}
                step={1}
                requiredMulti={true}
              />
              <div className="md:col-span-2">
                <SimpleInput
                  ref={input3Ref}
                  name="distance"
                  label={t('traceability.generalTab.distanceLabel')}
                  type="number"
                  value={values['distance'] as number}
                  onChange={(val) => onChange('distance', val)}
                  min={0}
                  step={0.1}
                  requiredMulti={true}
                />
              </div>
            </div>
          </RequiredMultiGroup>
        </div>
      </Card>

      <Card padding="sm">
        <div className="flex flex-col gap-2">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <SectionLabel icon={Gauge}>{t('traceability.generalTab.qualityTitle')}</SectionLabel>
              <p className="text-sm text-muted-foreground">
                {t('traceability.generalTab.qualityDescription')}
              </p>
            </div>
          </div>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <SimpleInput
              ref={input4Ref}
              name="malrond"
              label={t('traceability.generalTab.malroundLabel')}
              type="number"
              value={values['malrond'] as number}
              onChange={(val) => onChange('malrond', val)}
              placeholder={t('traceability.generalTab.malroundPlaceholder')}
            />
            <ComboboxInput
              name="operation"
              label={t('traceability.generalTab.operationLabel')}
              value={values['operation'] as string | null}
              onChange={(val) => onChange('operation', val)}
              options={OPERATION_OPTIONS}
              placeholder={t('traceability.generalTab.operationPlaceholder')}
            />
          </div>
        </div>
      </Card>

      <Card padding="sm">
        <div className="flex flex-col gap-2">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <SectionLabel icon={Settings2}>
                {t('traceability.generalTab.cutParameters.title')}
              </SectionLabel>
              <p className="text-sm text-muted-foreground">
                {t('traceability.generalTab.cutParameters.description')}
              </p>
            </div>
          </div>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <SimpleInput
              name="ae"
              label={t('traceability.generalTab.cutParameters.aeLabel')}
              type="number"
              value={values['ae'] as number}
              onChange={(val) => onChange('ae', val)}
              min={0}
              step={0.001}
              placeholder={t('traceability.generalTab.cutParameters.placeholder')}
            />
            <SimpleInput
              name="ap"
              label={t('traceability.generalTab.cutParameters.apLabel')}
              type="number"
              value={values['ap'] as number}
              onChange={(val) => onChange('ap', val)}
              min={0}
              step={0.001}
              placeholder={t('traceability.generalTab.cutParameters.placeholder')}
            />
            <SimpleInput
              name="vc"
              label={t('traceability.generalTab.cutParameters.vcLabel')}
              type="number"
              value={values['vc'] as number}
              onChange={(val) => onChange('vc', val)}
              min={0}
              step={0.001}
              placeholder={t('traceability.generalTab.cutParameters.placeholder')}
            />
            <SimpleInput
              name="fz"
              label={t('traceability.generalTab.cutParameters.fzLabel')}
              type="number"
              value={values['fz'] as number}
              onChange={(val) => onChange('fz', val)}
              min={0}
              step={0.001}
              placeholder={t('traceability.generalTab.cutParameters.placeholder')}
            />
          </div>
        </div>
      </Card>
    </div>
  );
});
